/**
 * Project State Slice
 *
 * Enterprise-grade project management with:
 * - Per-project floor plan memory (cross-session, cross-device via the server-
 *   synced project/floor-plan lists).
 * - Automatic floor plan restoration on project switch.
 * - IndexedDB cache of projects and floor plans (best-effort; never fatal).
 *
 * Cross-device correctness notes:
 *  - 'pinCam_lastProjectId' persists the last opened project so a returning
 *    session re-opens it on startup.
 *  - 'pinCam_lastFloorPlanByProject' is a JSON map { [projectId]: floorPlanId }
 *    remembering which floor plan was last active per project.
 *  - The IndexedDB cache is cleared and rewritten on every successful fetch so
 *    it never serves stale data; cache failures are swallowed (the network
 *    response is the source of truth).
 */

import api from '@/services/api/mobileApiService';
import * as idb from '../db';
import { ProjectState, ProjectActions } from '@/types/store';
import { Project, FloorPlan } from '@/types/index';
import { logger } from '@/utils/logger';
import { safeStorage } from '@/utils/safeStorage';

// Storage key for per-project floor plan memory.
// Shape on disk: JSON object mapping String(projectId) -> floorPlanId (number).
const FLOOR_PLAN_MEMORY_KEY = 'pinCam_lastFloorPlanByProject';

// Storage key for the last opened project (cross-session restoration).
const LAST_PROJECT_KEY = 'pinCam_lastProjectId';

/** JSON shape persisted under FLOOR_PLAN_MEMORY_KEY. */
type FloorPlanMemory = Record<string, number>;

/**
 * Minimal view of the cross-slice store surface this slice reaches into via
 * `get()`. Typed explicitly (rather than `any`) so misuse is caught at compile
 * time, while staying decoupled from the circular `AppState` type.
 */
interface ProjectSliceGetters extends ProjectState {
  // From the photo slice:
  floorPlanImage: (FloorPlan & Record<string, unknown>) | null;
  setFloorPlanImage: (image: FloorPlan | null) => void;
  // Project actions referenced re-entrantly:
  setCurrentProject: (project: Project | null) => void;
  fetchFloorPlans: (projectId: number) => Promise<void>;
}

type GetState = () => ProjectSliceGetters;

/**
 * Get the last selected floor plan ID for a project.
 * Returns null when no memory exists or the stored value is unreadable.
 */
function getLastFloorPlanForProject(projectId: number): number | null {
  try {
    const stored = safeStorage.getItem(FLOOR_PLAN_MEMORY_KEY);
    if (!stored) return null;
    const memory: FloorPlanMemory = JSON.parse(stored);
    return memory[String(projectId)] ?? null;
  } catch (error) {
    logger.warn('Failed to get last floor plan for project', { projectId, error });
    return null;
  }
}

/**
 * Save the last selected floor plan ID for a project.
 */
function saveLastFloorPlanForProject(projectId: number, floorPlanId: number): void {
  try {
    const stored = safeStorage.getItem(FLOOR_PLAN_MEMORY_KEY);
    const memory: FloorPlanMemory = stored ? JSON.parse(stored) : {};
    memory[String(projectId)] = floorPlanId;
    safeStorage.setItem(FLOOR_PLAN_MEMORY_KEY, JSON.stringify(memory));
    logger.debug('Saved floor plan for project', { projectId, floorPlanId });
  } catch (error) {
    logger.warn('Failed to save last floor plan for project', { projectId, floorPlanId, error });
  }
}

/**
 * Clear the saved floor plan for a project (when project is deleted).
 */
function clearLastFloorPlanForProject(projectId: number): void {
  try {
    const stored = safeStorage.getItem(FLOOR_PLAN_MEMORY_KEY);
    if (!stored) return;
    const memory: FloorPlanMemory = JSON.parse(stored);
    delete memory[String(projectId)];
    safeStorage.setItem(FLOOR_PLAN_MEMORY_KEY, JSON.stringify(memory));
  } catch (error) {
    logger.warn('Failed to clear floor plan for project', { projectId, error });
  }
}

/**
 * Resolve which floor plan should be active for a given project, given
 * the current `floorPlans[]` in the store, and apply it via
 * `setFloorPlanImage`.
 *
 * Selection order:
 *   1. The floor plan whose id matches `pinCam_lastFloorPlanByProject`
 *      for this project (cross-session memory).
 *   2. The first available floor plan for this project.
 *   3. null (no floor plan available).
 *
 * Called from two places that need identical behavior:
 *   - `setCurrentProject` — on every project switch.
 *   - `fetchFloorPlans` — at the end, so that a fire-and-forget fetch
 *     (e.g. from ProjectBottomSheet.select) eventually re-applies the
 *     selection once the network response arrives. Without this second
 *     call, the first project pick after a fresh install leaves
 *     `floorPlanImage` permanently null.
 */
function restoreFloorPlanForProject(get: GetState, projectId: number): void {
  const state = get();
  const lastFloorPlanId = getLastFloorPlanForProject(projectId);
  const floorPlans = state.floorPlans;
  const projectFloorPlans = floorPlans.filter((fp) => fp.project_id === projectId);

  let chosen: FloorPlan | null = null;
  if (lastFloorPlanId) {
    chosen = projectFloorPlans.find((fp) => fp.id === lastFloorPlanId) ?? null;
    if (chosen) {
      logger.info('Restoring last floor plan for project', {
        projectId,
        floorPlanId: chosen.id,
        floorPlanName: chosen.name,
      });
    } else if (projectFloorPlans.length > 0) {
      logger.info('Last floor plan not found, using first available', { projectId });
    }
  }
  if (!chosen && projectFloorPlans.length > 0) {
    if (!lastFloorPlanId) {
      logger.info('No saved floor plan, using first available', { projectId });
    }
    chosen = projectFloorPlans[0];
  }

  state.setFloorPlanImage(chosen);
}

export const projectSlice: ProjectState = {
  projects: [],
  currentProject: null,
  floorPlans: [],
};

export const projectActions: ProjectActions = (set, get) => ({
  setProjects: (projects: Project[]): void => set({ projects }),

  addProject: (project: Project): void =>
    set((state: ProjectState) => ({ projects: [...state.projects, project] })),

  deleteProject: (projectId: number): void => {
    // Clear floor plan memory for this project
    clearLastFloorPlanForProject(projectId);
    set((state: ProjectState) => ({
      projects: state.projects.filter((p) => p.id !== projectId),
    }));
  },

  setCurrentProject: (project: Project | null): void => {
    const state = get() as ProjectSliceGetters;
    const previousProject = state.currentProject;
    const currentFloorPlan = state.floorPlanImage;

    // Save current floor plan for the previous project (if any)
    if (previousProject && currentFloorPlan?.id) {
      saveLastFloorPlanForProject(previousProject.id, currentFloorPlan.id);
    }

    // Set the new project
    set({ currentProject: project });

    // Persist last project ID for cross-session restoration
    if (project) {
      safeStorage.setItem(LAST_PROJECT_KEY, String(project.id));
    } else {
      safeStorage.removeItem(LAST_PROJECT_KEY);
    }

    // Restore floor plan for the new project. If `floorPlans[]` is
    // still empty (e.g. first time this project is selected after a
    // fresh install — fetchFloorPlans is fire-and-forget from the
    // bottom-sheet picker), this call sets `floorPlanImage = null` and
    // the actual restoration runs again at the end of fetchFloorPlans.
    if (project) {
      restoreFloorPlanForProject(get as GetState, project.id);
    } else {
      // No project selected, clear floor plan
      state.setFloorPlanImage(null);
    }
  },

  updateCurrentProject: (updates: Partial<Project>): void =>
    set((state: ProjectState) => ({
      currentProject: state.currentProject ? { ...state.currentProject, ...updates } : null,
    })),

  setFloorPlans: (floorPlans: FloorPlan[]): void => set({ floorPlans }),

  addFloorPlan: (floorPlan: FloorPlan): void =>
    set((state: ProjectState) => ({ floorPlans: [...state.floorPlans, floorPlan] })),

  updateFloorPlan: (updatedFloorPlan: FloorPlan): void =>
    set((state: ProjectState) => ({
      floorPlans: state.floorPlans.map((fp) =>
        fp.id === updatedFloorPlan.id ? updatedFloorPlan : fp
      ),
    })),

  deleteFloorPlan: (floorPlanId: number): void =>
    set((state: ProjectState) => ({
      floorPlans: state.floorPlans.filter((fp) => fp.id !== floorPlanId),
    })),

  fetchProjects: async (): Promise<void> => {
    try {
      const rawProjects = await api.getProjects();
      const projects: Project[] = Array.isArray(rawProjects) ? rawProjects : [];
      logger.info('API Response', { projects });

      // Best-effort cache refresh: clear then repopulate. Failure is non-fatal.
      try {
        await idb.clear('projects');
        projects.forEach((project) => idb.set('projects', project));
      } catch (dbError) {
        logger.warn('IndexedDB operation failed, continuing without cache', { error: dbError });
      }

      set({ projects });

      // Restore last project across sessions if none is currently selected
      const state = get() as ProjectSliceGetters;
      if (!state.currentProject) {
        const lastId = safeStorage.getItem(LAST_PROJECT_KEY);
        if (lastId) {
          const last = projects.find((p) => String(p.id) === lastId);
          if (last) {
            try {
              await state.fetchFloorPlans(last.id);
            } catch {
              /* floor plan fetch failure is non-fatal */
            }
            state.setCurrentProject(last);
            logger.info('Restored last project on startup', {
              projectId: last.id,
              projectName: last.name,
            });
          }
        }
      }
    } catch (error: unknown) {
      logger.error('Error fetching projects', { error });
      try {
        await idb.clear('projects');
      } catch (clearError) {
        logger.warn('Error clearing cached projects', { error: clearError });
      }
      set({ projects: [] });
    }
  },

  fetchFloorPlans: async (projectId: number): Promise<void> => {
    try {
      const floorPlansData: FloorPlan[] = await api.getFloorPlans(projectId);
      logger.info('Floor plans API response', { floorPlansData });
      // Expose `image_path` as `url` for consumers that read `.url`.
      const floorPlansWithUrl: Array<FloorPlan & { url: string }> = (
        Array.isArray(floorPlansData) ? floorPlansData : []
      ).map((fp) => ({
        ...fp,
        url: fp.image_path,
      }));

      // Best-effort cache refresh: clear then repopulate. Failure is non-fatal.
      try {
        await idb.clear('floorPlans');
        floorPlansWithUrl.forEach((fp) => idb.set('floorPlans', fp));
      } catch (dbError) {
        logger.warn('IndexedDB operation failed, continuing without cache', { error: dbError });
      }

      // Merge with existing floor plans from other projects
      const state = get() as ProjectSliceGetters;
      const existingFloorPlans = state.floorPlans;
      const otherProjectFloorPlans = existingFloorPlans.filter(
        (fp) => fp.project_id !== projectId
      );
      const mergedFloorPlans: FloorPlan[] = [...otherProjectFloorPlans, ...floorPlansWithUrl];

      set({ floorPlans: mergedFloorPlans });

      // If this fetch was for the currently active project, re-run the
      // restoration helper. This fixes the first-launch race in which
      // setCurrentProject ran before this fetch returned, found an
      // empty floorPlans[], and set floorPlanImage to null. Now that
      // floorPlans[] is populated we can pick the right one — same
      // selection rules as setCurrentProject.
      const stateAfter = get() as ProjectSliceGetters;
      if (stateAfter.currentProject?.id === projectId) {
        restoreFloorPlanForProject(get as GetState, projectId);
      }
    } catch (error: unknown) {
      logger.error('Error fetching floor plans', { error });
      try {
        await idb.clear('floorPlans');
      } catch (clearError) {
        logger.warn('Error clearing cached floor plans', { error: clearError });
      }
      // Don't clear floor plans on error - keep existing ones
    }
  },
});
